// Desenho do card de divulgacao (1080px de largura) no canvas.
//
// Reproduz o formato dos cards que os corretores ja usam: foto no topo, barra
// laranja de 6px e faixa branca com bairro / endereco / valor / specs / obras.
// A geometria veio do gerador em PowerShell (make_cards.ps1), com uma troca:
// a fonte e Ubuntu (a mesma do site e do boletim) em vez de Verdana, porque
// Verdana nao existe em Android/iOS e o card sairia diferente em cada aparelho.

const LARANJA = 'rgb(234, 92, 29)';
const CINZA = 'rgb(110, 110, 110)';
const CINZA_CLARO = 'rgb(160, 160, 160)';
const ESCURO = 'rgb(51, 51, 51)';

const CW = 1080;
const X = 40;
const MAXW = CW - X * 2;
const BARRA = 6;

const fonte = (tamanho, peso = 400) => `${peso} ${tamanho}px Ubuntu, Roboto, Arial, sans-serif`;

export const CDN_FOTOS = process.env.REACT_APP_FILES_URL || 'https://files.visualizarimovel.com.br';

// As fontes precisam estar carregadas ANTES do primeiro fillText, senao o
// canvas desenha com a fallback e o card sai com metricas erradas.
// document.fonts.load quer um shorthand com UMA familia — passar a lista
// inteira faz o Firefox recusar a string.
export async function aguardarFontes() {
  if (!document.fonts) return;
  const pedidos = ['700 46px Ubuntu', '700 54px Ubuntu', '400 30px Ubuntu', '400 28px Ubuntu'];
  try {
    await Promise.all(pedidos.map((p) => document.fonts.load(p)));
    await document.fonts.ready;
  } catch (e) {
    // sem a Ubuntu o card sai na fallback do sistema; melhor isso que travar
  }
}

const moeda = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;

const plural = (n, singular, pluralForma) => `${n} ${n === 1 ? singular : pluralForma}`;

export function specsDoImovel(p) {
  const partes = [];
  if (p.fullLandSize) partes.push(`${p.fullLandSize}m²`);

  const quartos = Number(p.bedrooms) || 0;
  const suites = Number(p.suites) || 0;
  if (quartos) {
    let texto = plural(quartos, 'quarto', 'quartos');
    if (suites === quartos && suites === 1) texto += ' (suíte)';
    else if (suites > 0) texto += ` (${plural(suites, 'suíte', 'suítes')})`;
    partes.push(texto);
  }

  const banheiros = Number(p.bathrooms) || 0;
  if (banheiros) partes.push(plural(banheiros, 'banheiro', 'banheiros'));

  const vagas = Number(p.parkingSpots) || 0;
  partes.push(vagas ? plural(vagas, 'vaga', 'vagas') : 'sem vaga');

  return partes.join(' · ');
}

// Palavras de unidade vao em caixa baixa, como no boletim ("apto 605",
// "cobertura 301", "casa 18").
const UNIDADES = [
  'apto', 'apartamento', 'cobertura', 'casa', 'sala', 'loja',
  'garden', 'bloco', 'bl', 'unidade', 'duplex', 'lamina',
];
const CONECTIVOS = ['de', 'da', 'do', 'das', 'dos', 'e'];

const semAcento = (s) => s.normalize('NFD').replace(/[^\x20-\x7E]/g, '').toLowerCase();

export function normalizarComplemento(bruto) {
  const comp = (bruto || '').trim();
  if (!comp) return '';

  // o cadastro as vezes traz so o numero ("511"); o boletim escreve "apto 511"
  if (/^\d+$/.test(comp)) return `apto ${comp}`;

  // ja veio em caixa mista: o cadastro foi escrito a mao, respeita
  if (comp !== comp.toUpperCase()) return comp;

  // caixa alta ("APTO 205 GARDEN - NOVO HOTEL GLORIA") vira capitalizado,
  // senao o card fica gritando
  const palavras = comp.toLowerCase().split(' ').map((palavra, i) => {
    if (!palavra) return palavra;
    if (i > 0 && CONECTIVOS.includes(palavra)) return palavra;
    if (UNIDADES.includes(semAcento(palavra))) return palavra;
    return palavra.charAt(0).toUpperCase() + palavra.slice(1);
  });
  return palavras.join(' ');
}

export function enderecoDoImovel(p) {
  let texto = p.address || '';
  if (p.addressNumber) texto += `, ${p.addressNumber}`;
  const comp = normalizarComplemento(p.addressComplement);
  if (comp) texto += `, ${comp}`;
  return texto;
}

// So aparece quando o cadastro tem a linha "Obras:" em sellingNotes.
// Sem invencao: imovel sem o dado sai sem a linha.
export function obrasDoImovel(p) {
  const m = (p.sellingNotes || '').match(/Obras?\s*:\s*([^\n\r]+)/i);
  return m ? `Obras: ${m[1].trim()}` : null;
}

export function capaDoImovel(p) {
  const imagens = (p.images || []).filter((i) => i && i.image);
  if (!imagens.length) return null;
  const capa = imagens.slice().sort((a, b) => (a.order || 0) - (b.order || 0))[0];
  return `${CDN_FOTOS}/${p._id}/fotos/${capa.image}`;
}

const carregarFoto = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // sem isto o canvas fica "tainted" e o toBlob falha; o CDN responde
    // access-control-allow-origin quando a requisicao manda Origin
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('falha ao carregar a foto'));
    img.src = url;
  });

const quebrarLinhas = (ctx, texto, larguraMax) => {
  const linhas = [];
  let atual = '';
  texto.split(' ').forEach((palavra) => {
    const teste = atual ? `${atual} ${palavra}` : palavra;
    if (ctx.measureText(teste).width <= larguraMax) atual = teste;
    else {
      if (atual) linhas.push(atual);
      atual = palavra;
    }
  });
  if (atual) linhas.push(atual);
  return linhas;
};

/**
 * Desenha o card do imovel e devolve um Blob JPEG de 1080px de largura.
 */
export async function gerarCard(p) {
  const urlFoto = capaDoImovel(p);
  if (!urlFoto) throw new Error('imóvel sem foto de capa');

  const foto = await carregarFoto(urlFoto);
  const alturaFoto = Math.round((CW * foto.naturalHeight) / foto.naturalWidth);

  const bairro = p.neighborhoodName || '';
  const endereco = enderecoDoImovel(p);
  const obras = obrasDoImovel(p);
  const specs = specsDoImovel(p);

  const temDesconto = p.descountPrice && Number(p.descountPrice) < Number(p.sellingPrice);
  const valor = moeda(temDesconto ? p.descountPrice : p.sellingPrice);
  const valorAntigo = temDesconto ? `Era: ${moeda(p.sellingPrice)}` : null;

  // 1a passada: medir para saber a altura da faixa
  const medidor = document.createElement('canvas').getContext('2d');
  medidor.font = fonte(30);
  const linhasEndereco = quebrarLinhas(medidor, endereco, MAXW);

  // specs encolhem ate caber numa linha so
  let tamanhoSpecs = 28;
  while (tamanhoSpecs > 20) {
    medidor.font = fonte(tamanhoSpecs);
    if (medidor.measureText(specs).width <= MAXW) break;
    tamanhoSpecs -= 1;
  }

  const alturaFaixa =
    30 + 56 + linhasEndereco.length * 40 + 10 + 66 + 14 + 40 + (obras ? 8 + 40 : 0) + 30;

  const canvas = document.createElement('canvas');
  canvas.width = CW;
  canvas.height = alturaFoto + BARRA + alturaFaixa;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(foto, 0, 0, CW, alturaFoto);
  ctx.fillStyle = LARANJA;
  ctx.fillRect(0, alturaFoto, CW, BARRA);

  ctx.textBaseline = 'top';
  let y = alturaFoto + BARRA + 30;

  ctx.font = fonte(46, 700);
  ctx.fillStyle = LARANJA;
  ctx.fillText(bairro, X, y);
  y += 56;

  ctx.font = fonte(30);
  ctx.fillStyle = CINZA;
  linhasEndereco.forEach((linha) => {
    ctx.fillText(linha, X, y);
    y += 40;
  });

  y += 10;
  ctx.font = fonte(54, 700);
  ctx.fillStyle = ESCURO;
  ctx.fillText(valor, X, y);
  if (valorAntigo) {
    const larguraValor = ctx.measureText(valor).width;
    ctx.font = fonte(28);
    ctx.fillStyle = CINZA_CLARO;
    ctx.fillText(valorAntigo, X + larguraValor + 24, y + 26);
  }
  y += 66 + 14;

  ctx.font = fonte(tamanhoSpecs);
  ctx.fillStyle = CINZA;
  ctx.fillText(specs, X, y);
  y += 40;

  if (obras) {
    y += 8;
    ctx.font = fonte(28);
    ctx.fillStyle = CINZA;
    ctx.fillText(obras, X, y);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('falha ao gerar o JPEG'))),
      'image/jpeg',
      0.92
    );
  });
}

// ⚠️ Este site nao pode conter NENHUMA referencia a Imovel Vazio — nem no
// visivel, nem nos links que o corretor repassa ao cliente. Por isso o link do
// imovel aponta para o visualizador deste proprio dominio (/{id}), e nao para
// o site da marca.
export const SITE = 'https://www.visualizarimovel.com.br';

export const linkDoImovel = (p) => `${SITE}/${p._id}`;

// Legenda que acompanha o card no WhatsApp.
export function textoCompartilhar(p) {
  const temDesconto = p.descountPrice && Number(p.descountPrice) < Number(p.sellingPrice);
  const valor = moeda(temDesconto ? p.descountPrice : p.sellingPrice);
  return [
    `${p.neighborhoodName} — ${enderecoDoImovel(p)}`,
    `${valor} · ${specsDoImovel(p)}`,
    linkDoImovel(p),
  ].join('\n');
}

// "Botafogo - Rua Assis Bueno, 46.jpg"
// Sem acento nem caractere especial: o nome viaja por WhatsApp, Android e iOS.
// Sem sufixo de marca, pelo mesmo motivo do link acima.
export function nomeDoArquivo(p) {
  const bruto = `${p.neighborhoodName || ''} - ${p.address || ''}, ${p.addressNumber || ''}`;
  const semAcento = bruto.normalize('NFD').replace(/[^\x20-\x7E]/g, '');
  const limpo = semAcento
    .replace(/[^\w\s,.-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return `${limpo}.jpg`;
}
